import { mutationAtomWithLoadable } from 'lib/atoms';
import request from 'lib/request';
import type { AcreageDriftResponse, AcreageThresholds } from './types';

/**
 * Runs the audit and answers with the report.
 *
 * One request, synchronously. This went through a background job for a while,
 * on a measurement of 8.8s taken on sandbox; measured again on both
 * environments the scan is 0.7s cold on the production primary and 0.47s on
 * sandbox, so the job was machinery around a sub-second query.
 *
 * Always scans the whole population at these thresholds. The tier and stat
 * chips filter what has already been fetched, so switching between them costs
 * nothing; the thresholds cannot work that way, because loosening one asks for
 * rows the scan never returned.
 *
 * @param {AcreageThresholds} thresholds - The bars to judge each tier at.
 * @return {Promise<object>} The report.
 */
export const [runAuditAtom, runAuditLoadableAtom] = mutationAtomWithLoadable<
  AcreageDriftResponse,
  [AcreageThresholds]
>(async (_get, _set, thresholds) => {
  const abortController = new AbortController();
  const response = await request.post<AcreageDriftResponse, { error: string }>(
    '/admin/subtract_acreage_drift/run',
    {
      shortfall_percent: thresholds.shortfallPercent,
      contradicted_multiple: thresholds.contradictedMultiple,
    },
    abortController
  );

  if (response.type === 'error') {
    throw new Error(response.data?.error ?? 'The audit failed');
  }

  if (response.type === 'fatal' || response.type === 'abort') {
    throw new Error(response.message);
  }

  return (response as { type: 'success'; data: AcreageDriftResponse }).data;
});
